(() => {
  if (window.ImpressikoTicker) {
    window.ImpressikoTicker.initAll(document);
    return;
  }

  const instances = new WeakMap();

  function init(root) {
    if (instances.has(root)) return;
    const viewport = root.querySelector('.image-title-ticker__viewport');
    const track = root.querySelector('.image-title-ticker__track');
    const sourceGroup = root.querySelector('[data-itt-group="source"]');
    if (!viewport || !track || !sourceGroup) return;

    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const desktopColumns = Number(root.dataset.columnsDesktop) || 6;
    const mobileColumns = Number(root.dataset.columnsMobile) || 2;
    const customWidthDesktop = Number(root.dataset.customWidthDesktop) || 240;
    const customWidthMobile = Number(root.dataset.customWidthMobile) || 160;
    const pixelsPerSecond = Number(root.dataset.speed) || 60;
    const onFocusIn = () => root.classList.add('is-paused');
    const onFocusOut = () => {
      if (!root.contains(document.activeElement)) root.classList.remove('is-paused');
    };
    let resizeFrame = 0;
    let resizeObserver;

    const removeClones = () => {
      track.querySelectorAll('[data-itt-group="clone"]').forEach((clone) => clone.remove());
    };

    const createClone = () => {
      const clone = sourceGroup.cloneNode(true);
      clone.dataset.ittGroup = 'clone';
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('role');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((item) => {
        item.removeAttribute('data-shopify-editor-block');
        item.removeAttribute('role');
      });
      track.appendChild(clone);
    };

    const update = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        if (!root.isConnected) return;
        root.dataset.ready = 'false';
        removeClones();

        const isMobile = mobileQuery.matches;
        const gap = parseFloat(window.getComputedStyle(root).getPropertyValue('--itt-gap')) || 0;
        const requestedColumns = isMobile ? mobileColumns : desktopColumns;
        // Product-info blocks are much narrower than standalone sections. Keep
        // cards readable while treating the editor's column count as a maximum.
        const fittingColumns = root.dataset.fitProductColumn === 'true'
          ? Math.max(1, Math.floor((viewport.clientWidth + gap) / ((isMobile ? 100 : 110) + gap)))
          : requestedColumns;
        const columns = Math.min(requestedColumns, fittingColumns);
        const widthMode = root.dataset.widthMode || 'columns';
        let itemWidth;

        if (widthMode === 'custom') {
          itemWidth = isMobile ? customWidthMobile : customWidthDesktop;
        } else {
          const firstCard = sourceGroup.querySelector('.image-title-ticker__card');
          const cardStyles = firstCard ? window.getComputedStyle(firstCard) : null;
          const outerMargin = cardStyles
            ? (parseFloat(cardStyles.marginLeft) || 0) + (parseFloat(cardStyles.marginRight) || 0)
            : 0;
          itemWidth = Math.max(1, (viewport.clientWidth - gap * (columns - 1) - outerMargin * columns) / columns);
        }

        root.style.setProperty('--itt-item-width', `${Math.max(1, itemWidth)}px`);
        const groupWidth = sourceGroup.getBoundingClientRect().width;
        if (!groupWidth) return;

        const repeatCount = Math.max(2, Math.ceil(viewport.clientWidth / (groupWidth + gap)) + 2);
        for (let index = 1; index < repeatCount; index += 1) createClone();

        root.style.setProperty('--itt-distance', `${-(groupWidth + gap)}px`);
        root.style.setProperty('--itt-duration', `${Math.max(4, (groupWidth + gap) / pixelsPerSecond)}s`);
        // Restart after section resize so the loop never pauses between settings changes.
        void track.offsetWidth;
        root.dataset.ready = 'true';
      });
    };

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(viewport);
    } else {
      window.addEventListener('resize', update);
    }
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', update);
    else mobileQuery.addListener(update);
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);

    instances.set(root, {
      destroy() {
        window.cancelAnimationFrame(resizeFrame);
        resizeObserver?.disconnect();
        if (!resizeObserver) window.removeEventListener('resize', update);
        if (mobileQuery.removeEventListener) mobileQuery.removeEventListener('change', update);
        else mobileQuery.removeListener(update);
        root.removeEventListener('focusin', onFocusIn);
        root.removeEventListener('focusout', onFocusOut);
        removeClones();
        instances.delete(root);
      }
    });
    update();
  }

  function initAll(scope = document) {
    if (scope.matches?.('.image-title-ticker')) init(scope);
    scope.querySelectorAll?.('.image-title-ticker').forEach(init);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const scope = event.target;
    const roots = scope.matches?.('.image-title-ticker') ? [scope] : [...scope.querySelectorAll('.image-title-ticker')];
    roots.forEach((root) => instances.get(root)?.destroy());
  });
  document.addEventListener('shopify:block:select', (event) => {
    event.target.closest?.('.image-title-ticker')?.classList.add('is-paused');
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    event.target.closest?.('.image-title-ticker')?.classList.remove('is-paused');
  });

  // The controller receives disconnectedCallback when Dawn replaces product
  // information after a variant change, even without a Shopify editor event.
  if (!customElements.get('impressiko-ticker-controller')) {
    customElements.define('impressiko-ticker-controller', class extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('.image-title-ticker');
        if (this.root) init(this.root);
      }
      disconnectedCallback() {
        if (this.root) instances.get(this.root)?.destroy();
      }
    });
  }

  window.ImpressikoTicker = { initAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  else initAll(document);
})();
